import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import type { EventObjectRow, ObjectGroupRow } from '@/db/types'
import { getDeviceId } from './syncStateRepository'
import { reassignObject } from './entryRepository'
import type { ChangeSet } from './entryRepository'

/**
 * Event objects: the second dimension.
 *
 * Orthogonal to event types on purpose. "Three hours of Coding" and "three
 * hours on Project X" are different questions, and this is what lets one entry
 * answer both.
 *
 * Every write sets updatedAt, deviceId and _dirty, and deletes are soft --
 * the same rules as everywhere else, for the same reason.
 */

export async function listObjects(
  includeArchived = false,
  db: BlockyTimeDB = defaultDb,
): Promise<EventObjectRow[]> {
  const rows = await db.eventObjects.orderBy('sortOrder').toArray()
  return rows.filter((r) => r.deletedAt === null && (includeArchived || r.isArchived === 0))
}

export async function listGroups(db: BlockyTimeDB = defaultDb): Promise<ObjectGroupRow[]> {
  const rows = await db.objectGroups.orderBy('sortOrder').toArray()
  return rows.filter((r) => r.deletedAt === null && r.isArchived === 0)
}

export async function createObject(
  name: string,
  groupId: string | null = null,
  db: BlockyTimeDB = defaultDb,
): Promise<EventObjectRow> {
  const trimmed = name.trim()
  if (!trimmed) throw new Error('An object needs a name')

  const deviceId = await getDeviceId(db)
  const now = Date.now()
  const existing = await db.eventObjects.count()

  const row: EventObjectRow = {
    id: crypto.randomUUID(),
    groupId,
    name: trimmed,
    colorHex: null,
    icon: null,
    sortOrder: existing,
    isArchived: 0,
    updatedAt: now,
    deletedAt: null,
    deviceId,
    serverSeq: 0,
    _dirty: 1,
  }

  await db.eventObjects.put(row)
  return row
}

export async function updateObject(
  id: string,
  patch: Partial<Pick<EventObjectRow, 'name' | 'groupId' | 'colorHex' | 'sortOrder' | 'isArchived'>>,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  const deviceId = await getDeviceId(db)
  const row = await db.eventObjects.get(id)
  if (!row) return

  await db.eventObjects.put({
    ...row,
    ...patch,
    updatedAt: Date.now(),
    deviceId,
    _dirty: 1,
  })
}

export async function archiveObject(id: string, db: BlockyTimeDB = defaultDb): Promise<void> {
  await updateObject(id, { isArchived: 1 }, db)
}

/**
 * Soft-deletes an object and detaches it from its entries.
 *
 * Entries survive: losing an hour of tracked time because you tidied up a
 * project list would be indefensible. They simply lose their object.
 */
export async function deleteObject(
  id: string,
  db: BlockyTimeDB = defaultDb,
): Promise<ChangeSet> {
  const deviceId = await getDeviceId(db)
  const now = Date.now()

  const change = await reassignObject(id, null, db)

  const row = await db.eventObjects.get(id)
  if (row) {
    await db.eventObjects.put({ ...row, deletedAt: now, updatedAt: now, deviceId, _dirty: 1 })
  }

  return change
}

/**
 * Merges one object into another.
 *
 * Every entry pointing at the source is repointed at the target, then the
 * source is tombstoned. Returns the entry ChangeSet so the whole merge can be
 * undone as a single operation -- a merge that could only be half-undone would
 * be worse than none.
 */
export async function mergeObjects(
  sourceId: string,
  targetId: string,
  db: BlockyTimeDB = defaultDb,
): Promise<ChangeSet> {
  if (sourceId === targetId) return { before: [], after: [] }

  const target = await db.eventObjects.get(targetId)
  if (!target || target.deletedAt !== null) {
    throw new Error('Cannot merge into an object that does not exist')
  }

  const change = await reassignObject(sourceId, targetId, db)

  const deviceId = await getDeviceId(db)
  const now = Date.now()
  const source = await db.eventObjects.get(sourceId)
  if (source) {
    await db.eventObjects.put({
      ...source,
      deletedAt: now,
      updatedAt: now,
      deviceId,
      _dirty: 1,
    })
  }

  return change
}

export async function createGroup(
  name: string,
  db: BlockyTimeDB = defaultDb,
): Promise<ObjectGroupRow> {
  const trimmed = name.trim()
  if (!trimmed) throw new Error('A group needs a name')

  const deviceId = await getDeviceId(db)
  const existing = await db.objectGroups.count()

  const row: ObjectGroupRow = {
    id: crypto.randomUUID(),
    name: trimmed,
    sortOrder: existing,
    isArchived: 0,
    updatedAt: Date.now(),
    deletedAt: null,
    deviceId,
    serverSeq: 0,
    _dirty: 1,
  }

  await db.objectGroups.put(row)
  return row
}

/** How many live entries point at an object. Shown before a destructive action. */
export async function countEntriesForObject(
  objectId: string,
  db: BlockyTimeDB = defaultDb,
): Promise<number> {
  const rows = await db.entries.where('eventObjectId').equals(objectId).toArray()
  return rows.filter((r) => r.deletedAt === null).length
}

/** Case-insensitive search, for the picker. */
export function searchObjects(objects: readonly EventObjectRow[], query: string): EventObjectRow[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...objects]
  return objects.filter((o) => o.name.toLowerCase().includes(q))
}

/** True when no existing object has this exact name, so "Create" should show. */
export function isNewObjectName(
  objects: readonly EventObjectRow[],
  query: string,
): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return false
  return !objects.some((o) => o.name.toLowerCase() === q)
}
